import React from 'react';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@roger/ui-components';
import { Plus, X } from 'lucide-react';
import AddressForm from './AddressForm';
import ContactInformationForm from './ContactInformationForm';
import type { ProvidersFormProps } from '../types';
import { NpiInput } from '../../NpiInput';

const providerTypeOptions = [
  { value: 'BillingProvider', label: 'Billing Provider' },
  { value: 'AttendingProvider', label: 'Attending Provider' },
  { value: 'ReferringProvider', label: 'Referring Provider' },
  { value: 'RenderingProvider', label: 'Rendering Provider' },
  { value: 'SupervisingProvider', label: 'Supervising Provider' },
];

const ProvidersForm: React.FC<ProvidersFormProps> = ({
  providers,
  onProviderChange,
  onProviderAddressChange,
  onAddProvider,
  onRemoveProvider,
}) => {
  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <h3 className='text-lg font-semibold'>Providers</h3>
        <Button
          type='button'
          onClick={onAddProvider}
          size='sm'
          variant='outline'
        >
          <Plus className='w-4 h-4' />
          Add Provider
        </Button>
      </div>

      {providers.map((provider, index) => (
        <Accordion
          key={index}
          type='single'
          collapsible
          className='border rounded-lg'
        >
          <AccordionItem value={`provider-${index}`}>
            <AccordionTrigger className='px-4'>
              <div className='flex items-center justify-between w-full'>
                <span>
                  {provider.providerType || 'New Provider'} -{' '}
                  {provider.organizationName ||
                    provider.firstName + ' ' + provider.lastName ||
                    'Unnamed'}
                </span>
                <Button
                  type='button'
                  onClick={e => {
                    e.stopPropagation();
                    onRemoveProvider(index);
                  }}
                  size='sm'
                  variant='outline'
                  className='ml-2'
                >
                  <X className='w-4 h-4' />
                </Button>
              </div>
            </AccordionTrigger>
            <AccordionContent className='px-4 pb-4'>
              <div className='space-y-4'>
                {/* Provider Type and NPI */}
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <label className='block text-sm font-medium mb-1'>
                      Provider Type <span className='text-red-500'>*</span>
                    </label>
                    <Select
                      value={provider.providerType || ''}
                      onValueChange={value =>
                        onProviderChange(index, 'providerType', value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder='Select provider type' />
                      </SelectTrigger>
                      <SelectContent>
                        {providerTypeOptions.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className='block text-sm font-medium mb-1'>
                      NPI <span className='text-red-500'>*</span>
                    </label>
                    <NpiInput
                      value={provider.npi || ''}
                      onValueChange={v => onProviderChange(index, 'npi', v)}
                      placeholder='National Provider Identifier'
                      required
                    />
                  </div>
                </div>

                {/* Organization or Individual */}
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <label className='block text-sm font-medium mb-1'>
                      Organization Name
                    </label>
                    <Input
                      value={provider.organizationName || ''}
                      onChange={e =>
                        onProviderChange(
                          index,
                          'organizationName',
                          e.target.value
                        )
                      }
                      placeholder='Organization name'
                    />
                  </div>

                  <div>
                    <label className='block text-sm font-medium mb-1'>
                      Employer ID
                    </label>
                    <Input
                      value={provider.employerId || ''}
                      onChange={e =>
                        onProviderChange(index, 'employerId', e.target.value)
                      }
                      placeholder='EIN/TIN'
                    />
                  </div>
                </div>

                {/* Secondary Identification */}
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <label className='block text-sm font-medium mb-1'>
                      Secondary ID Qualifier
                    </label>
                    <Input
                      value={
                        provider.secondaryIdentificationQualifierCode || ''
                      }
                      onChange={e =>
                        onProviderChange(
                          index,
                          'secondaryIdentificationQualifierCode',
                          e.target.value
                        )
                      }
                      placeholder='Type of secondary ID'
                    />
                  </div>

                  <div>
                    <label className='block text-sm font-medium mb-1'>
                      Secondary Identifier
                    </label>
                    <Input
                      value={provider.secondaryIdentifier || ''}
                      onChange={e =>
                        onProviderChange(
                          index,
                          'secondaryIdentifier',
                          e.target.value
                        )
                      }
                      placeholder='Secondary identifier value'
                    />
                  </div>
                </div>

                {/* Taxonomy Code */}
                <div>
                  <label className='block text-sm font-medium mb-1'>
                    Taxonomy Code
                  </label>
                  <Input
                    value={provider.taxonomyCode || ''}
                    onChange={e =>
                      onProviderChange(index, 'taxonomyCode', e.target.value)
                    }
                    placeholder='Provider taxonomy code'
                  />
                </div>

                {/* Individual Provider Names */}
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <label className='block text-sm font-medium mb-1'>
                      First Name
                    </label>
                    <Input
                      value={provider.firstName || ''}
                      onChange={e =>
                        onProviderChange(index, 'firstName', e.target.value)
                      }
                      placeholder='First name'
                    />
                  </div>

                  <div>
                    <label className='block text-sm font-medium mb-1'>
                      Last Name
                    </label>
                    <Input
                      value={provider.lastName || ''}
                      onChange={e =>
                        onProviderChange(index, 'lastName', e.target.value)
                      }
                      placeholder='Last name'
                    />
                  </div>
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <label className='block text-sm font-medium mb-1'>
                      Middle Name
                    </label>
                    <Input
                      value={provider.middleName || ''}
                      onChange={e =>
                        onProviderChange(index, 'middleName', e.target.value)
                      }
                      placeholder='Middle name'
                    />
                  </div>

                  <div>
                    <label className='block text-sm font-medium mb-1'>
                      Suffix
                    </label>
                    <Input
                      value={provider.suffix || ''}
                      onChange={e =>
                        onProviderChange(index, 'suffix', e.target.value)
                      }
                      placeholder='Suffix (Jr., Sr., etc.)'
                    />
                  </div>
                </div>

                {/* Address */}
                <div>
                  <h4 className='text-sm font-semibold mb-2'>Address</h4>
                  {provider.address ? (
                    <AddressForm
                      address={provider.address}
                      onChange={(field, value) =>
                        onProviderAddressChange(index, field, value)
                      }
                      required
                    />
                  ) : (
                    <div className='text-sm text-gray-500 p-4 border border-dashed border-gray-300 rounded'>
                      No address provided
                      <button
                        type='button'
                        onClick={() =>
                          onProviderChange(index, 'address', {
                            address1: '',
                            city: '',
                            state: '',
                            postalCode: '',
                          })
                        }
                        className='ml-2 text-blue-600 hover:text-blue-800 underline'
                      >
                        Add Address
                      </button>
                    </div>
                  )}
                </div>

                {/* Contact Information */}
                <div>
                  <h4 className='text-sm font-semibold mb-2'>
                    Contact Information
                  </h4>
                  <ContactInformationForm
                    contactInformation={provider.contactInformation || {}}
                    onChange={(field, value) => {
                      onProviderChange(index, 'contactInformation', {
                        ...provider.contactInformation,
                        [field]: value,
                      });
                    }}
                  />
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      ))}

      {providers.length === 0 && (
        <div className='text-center py-8 text-gray-500'>
          No providers added. Click "Add Provider" to get started.
        </div>
      )}
    </div>
  );
};

export default ProvidersForm;
